import { useContext, useState, useRef, useEffect } from "react";
import { Box, TextField as TextArea, MenuItem } from "@mui/material";
import { StoreContext } from "./../../store/Store";
import { useForm, Controller } from "react-hook-form";
import {
  actionDialogType,
  MediaImageUploader,
  RouteLeavingGuard,
  SimpleActionDialog,
} from "./../../components";
import { BiUserX, BiSad } from "react-icons/bi";
import { useLogout } from "react-admin";
import { profileNames, userStatus } from "../../dataProvider/interfaces/IUser";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import { APIContext } from "../../store/APIStore";
import IDeveloperAccount from "../../dataProvider/interfaces/IDeveloperAccount";
// import { Admin, Resource } from "react-admin";
import { theme } from "./../../theme";
import { useHasPermissions } from "../../hooks/useHasPermissions";
import { PERMISSIONS } from "../../roles";
import { useNavigate } from "react-router";
import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import FdButton from "../../components/UIComponents/Button/FdButton";
import FdSelect from "../../components/UIComponents/Select/FdSelect";
import FdTextField from "../../components/UIComponents/TextField/FdTextfield";
import { profileViewStyle as classes } from "./styles";
import posthog from "posthog-js";

const ProfileView = () => {
  const logout = useLogout();
  const [state, dispatch] = useContext(StoreContext);
  const api = useContext(APIContext);
  const [attachedImage, setAttachedImage] = useState<File>();
  const [
    ,
    // dirtyForm
    setDirtyForm,
  ] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState<any>({
    open: false,
    message: "",
    type: actionDialogType.REGULAR,
    isLoading: false,
    onCancel: null,
  });
  const [developerAccounts, setDeveloperAccounts] = useState<
    IDeveloperAccount[]
  >([]);
  // let submit: Function;
  const notificationRef = useRef<any>();
  let navigate = useNavigate();

  // permissions
  const canManageDevAccount = useHasPermissions(
    PERMISSIONS.CAN_MANAGE_DEV_ACCOUNT
  );

  useEffect(() => {
    if (canManageDevAccount) {
      fetchDeveloperAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageDevAccount]);

  const fetchDeveloperAccount = async () => {
    try {
      const accounts = await api.developerAccountService.getDeveloperAccount(
        state.currentUser?.id
      );
      setDeveloperAccounts(accounts);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const onSubmit = async (values: any) => {
    let userImage;
    setDirtyForm(false);
    try {
      if (attachedImage) {
        const imageData = (await api.fileService.uploadImageFile("users", {
          resourceId: state.currentUser?.id,
          file: attachedImage,
        })) as any;

        userImage = imageData.image;
      }

      const response = await api.userService.update({
        ...values,
        id: state.currentUser?.id,
        image: userImage,
      });
      dispatch({ type: "SET_CURRENT_USER", payload: response });
      notificationRef.current?.show(
        "Profile updated successfully",
        notificationType.Success
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  // async function validate(values: any) {
  //   return;
  // }

  const onDeleteClick = () => {
    setConfirmationDialog({
      open: true,
      message: "Are you sure you want to delete your profile?",
      icon: <BiUserX size="45" />,
      type: actionDialogType.WARNING,
      isLoading: false,
      actionCallback: () => {
        setDirtyForm(false);
        handleDeleteProfile();
      },
      onCancel: handleDialogClose,
      onClose: handleDialogClose,
    });
  };

  const onLogoutClick = () => {
    setConfirmationDialog({
      open: true,
      message: "Are you sure you want to logout?",
      type: actionDialogType.WARNING,
      isLoading: false,
      actionCallback: handleLogout,
      onCancel: handleDialogClose,
      onClose: handleDialogClose,
    });
  };

  const handleLogout = () => {
    dispatch({ type: "SET_CURRENT_USER", payload: null });
    posthog.reset();
    logout();
  };

  const handleSendFeedback = async () => {
    // TODO send feedback to api
    // const feedback = (document.getElementById(
    //   "feedbackInput"
    // ) as HTMLInputElement)?.value;

    try {
      // delete user
      await api.userService.delete(state.currentUser?.id);

      // logout
      handleLogout();
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleDeleteProfile = async () => {
    try {
      // TODO call delete profile endpoint

      // show the feedback dialog
      setConfirmationDialog({
        ...confirmationDialog,
        open: true,
        type: actionDialogType.REGULAR,
        message: "We are sad to see you go.",
        icon: <BiSad size="45" />,
        content: (
          <Box>
            If you are not happy with our services, please leave a feedback, so
            we can improve.
            <TextArea
              id="feedbackInput"
              name="feedbackInput"
              placeholder="Any feedback?"
              multiline
              rows={6}
              //TODO: commented for mui v5
              // rowsMax={6}
            />
          </Box>
        ),
        isLoading: false,
        confirmLabel: "Done",
        actionCallback: handleSendFeedback,
        onCancel: null,
        onClose: handleLogout,
      });

      //   setConfirmationDialog({
      //     ...confirmationDialog,
      //     open: true,
      //     message: "Deleting event. This can take several minutes.",
      //     isLoading: true,
      //   });
      //   const { job } = (await EventService.delete(
      //     state.selectedEvent?.id
      //   )) as any;
      //   jobChecker = setInterval(() => {
      //     if (state.selectedEvent) {
      //       checkJobStatus(state.selectedEvent.id, job);
      //     }
      //   }, 3000); // check for the delete job status every 3s
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleDialogClose = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: false,
    });
  };

  const getStatusList = () => {
    return (Object.keys(userStatus) as Array<keyof typeof userStatus>).map(
      (key) => {
        const value = userStatus[key];
        return (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        );
      }
    );
  };

  const getProfileList = () => {
    const currentProfileIndex = (
      Object.values(profileNames) as string[]
    ).indexOf(state.currentUser?.profile.name);
    return (Object.keys(profileNames) as Array<keyof typeof profileNames>)
      .filter((key, index) => index <= currentProfileIndex) // We should hide any roles that have higher privileges than your own
      .map((key, index) => {
        const value = profileNames[key];
        const label = value
          .split("_")
          .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
          .join(" ");
        return (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        );
      });
  };
  const profileList = getProfileList();

  const statusList = getStatusList();

  const initialValues = {
    username: state.currentUser?.username,
    password: state.currentUser?.password,
    fullName: state.currentUser?.fullName,
    email: state.currentUser?.email,
    profile: state.currentUser?.profile.name,
    status: state.currentUser?.status,
  };

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty, isValid },
  } = useForm({
    defaultValues: initialValues,
  });
  console.log("initialValues", initialValues, isDirty);

  useEffect(() => {
    reset(initialValues)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[reset, state])

  return (
    <>
      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
      >
        <Box sx={classes.topActions}>
          <Box sx={classes.infoTitle}>
            <Box sx={classes.userImage}>
              <MediaImageUploader
                onImageSelect={(file: File) => {
                  setAttachedImage(file);
                }}
                imageUrl={state.currentUser?.image}
              />
            </Box>

            <Box textAlign="left">
              <FdTypography
                label={state.currentUser?.fullName}
                variant="h2"
                component="h2"
                style={{ marginBottom: "10px" }}
              />

              <FdTypography
                label={state.currentUser?.profile.name
                  .split("_")
                  .map(
                    (name: any) => name.charAt(0).toUpperCase() + name.slice(1)
                  )
                  .join(" ")}
              />

              <FdTypography label={state.currentUser?.account.name} />
              {developerAccounts[0] && (
                <Box marginTop="10px">
                  <Box fontWeight="bold">Dev info: </Box>
                  <Box display="flex" alignItems="center">
                    <FdTypography
                    label="API Key:" /> &nbsp;
                    <FdTypography
                      sx={{fontWeight:"600"}}
                      label={`${developerAccounts[0]?.apiKey}`}
                    />
                  </Box>

                  <FdTypography
                    label={` Max number of calls per month:${" "}
                    ${developerAccounts[0]?.maxNbrOfCallsPerMonth}`}
                  />

                  <FdTypography
                    label={` Number of calls this month:${" "}
                    ${developerAccounts[0]?.numOfCallsThisMonth}`}
                  />
                  <FdTypography
                    label={`Active: ${
                      developerAccounts[0]?.active ? "Yes" : "No"
                    }`}
                  />
                  <FdTypography
                    label={`Member of: ${developerAccounts[0]?.owner.account.name}`}
                  />
                  {developerAccounts.length > 1 && (
                    <FdTypography
                      sx={{ color: theme.palette.error.main }}
                      label="+ others"
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <FdButton
            ButtonName="Save"
            size="medium"
            form="profileForm"
            type="submit"
            sx={classes.saveButton}
            disabled={!isValid}
          />
        </Box>
        <Box sx={classes.formContent}>
          <form id="profileForm" onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* username input */}
            <FdTextField
              register={register}
              name="username"
              label="Username"
              disabled
              autoComplete="username"
            />

            {/* password input */}
            <FdTextField
              register={register}
              name="password"
              label="Password"
              type="password"
              autoComplete="current-password"
            />

            {/* full name input */}
            <FdTextField
              register={register}
              name="fullName"
              label="Full name"
              required
              error={errors.fullName?.type === "required"}
              errorMsg={errors.fullName?.type && "This field is required"}
            />

            {/* email input */}
            <FdTextField
              register={register}
              name="email"
              label="Email"
              type="email"
              required
              error={errors.email?.type === "required"}
              errorMsg={errors.email?.type && "This field is required"}
            />

            {/* Role select   */}
            <Controller
              {...register("profile")}
              control={control}
              defaultValue={initialValues.profile}
              render={({ field: { onChange, value } }) => (
                <FdSelect
                  name="profile"
                  label="Role"
                  required={false}
                  onChange={onChange}
                  value={value}
                >
                  {profileList}
                </FdSelect>
              )}
            />

            {/* Status select */}
            <Box paddingTop="15px">
              <Controller
                {...register("status")}
                control={control}
                defaultValue={initialValues.status}
                render={({ field: { onChange, value } }) => (
                  <FdSelect
                    name="status"
                    label="Status"
                    required={false}
                    onChange={onChange}
                    value={value}
                  >
                    {statusList}
                  </FdSelect>
                )}
              />
            </Box>
            {/* this is for showing a warning when leaving the form with unsaved updates */}
            <RouteLeavingGuard
              when={isDirty}
              navigate={(path) => navigate(path)}
              shouldBlockNavigation={(location) => isDirty}
            />
          </form>
        </Box>
        <Box sx={classes.bottomActions}>
          <FdButton
            startIcon={<BiUserX />}
            onClick={onDeleteClick}
            ButtonName="Delete my profile"
            variant="text"
          />
          <FdButton
            variant="text"
            onClick={onLogoutClick}
            ButtonName="Logout"
          />
        </Box>
      </Box>

      {confirmationDialog.open && (
        <SimpleActionDialog
          type={confirmationDialog.type}
          icon={confirmationDialog.icon}
          open={confirmationDialog.open}
          title={confirmationDialog.message}
          isLoading={confirmationDialog.isLoading}
          onConfirm={confirmationDialog.actionCallback as Function}
          content={confirmationDialog.content}
          confirmLabel={confirmationDialog.confirmLabel}
          onCancel={confirmationDialog.onCancel}
          onClose={confirmationDialog.onClose}
        />
      )}

      <NotificationMessage ref={notificationRef} />
    </>
  );
};

export default ProfileView;
